import React, { useState, useEffect } from 'react';
import { Coins, Trophy, Flame, ChevronRight, Star, Clock } from 'lucide-react';
import Confetti from './Confetti';

const SessionSummary = ({ answers, coinsEarned, totalTime, onContinue, beatYesterdayBonus, yesterdayScore, bossResult }) => {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const correctCount = answers?.filter(a => a.correct).length || 0;
  const totalCount = answers?.length || 0;
  const accuracy = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden" data-testid="session-summary">
      {showConfetti && <Confetti />}
      
      <div className="w-full max-w-md space-y-6 animate-pop">
        {/* Celebration Header */}
        <div className="text-center">
          <div className="text-8xl mb-4">🎉</div>
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 font-fredoka mb-2">
            Session Complete!
          </h1>
          <p className="text-xl text-slate-600">You did amazing today!</p>
        </div>

        {/* Coins Earned */}
        <div className="card-bubbly bg-gradient-to-br from-primary/20 to-primary/10 text-center" data-testid="coins-earned-card">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center animate-coin-spin">
              <Coins className="w-8 h-8 text-black" />
            </div>
          </div>
          <p className="text-4xl font-black text-slate-900 mb-1">
            +{coinsEarned?.total || 0} Brain Coins
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-sm text-slate-600">
            {coinsEarned?.base > 0 && (
              <span className="bg-white/50 px-2 py-1 rounded-full">Completion: +{coinsEarned.base}</span>
            )}
            {coinsEarned?.accuracy_bonus > 0 && (
              <span className="bg-white/50 px-2 py-1 rounded-full">Accuracy: +{coinsEarned.accuracy_bonus}</span>
            )}
            {coinsEarned?.speed_bonus > 0 && (
              <span className="bg-white/50 px-2 py-1 rounded-full">Speed: +{coinsEarned.speed_bonus}</span>
            )}
            {coinsEarned?.effort_bonus > 0 && (
              <span className="bg-white/50 px-2 py-1 rounded-full">Effort: +{coinsEarned.effort_bonus}</span>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="card-bubbly text-center py-4">
            <Star className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-black text-slate-900">{accuracy}%</p>
            <p className="text-xs text-slate-500">Accuracy</p>
          </div>
          
          <div className="card-bubbly text-center py-4">
            <Trophy className="w-6 h-6 text-secondary mx-auto mb-2" />
            <p className="text-2xl font-black text-slate-900">{correctCount}/{totalCount}</p>
            <p className="text-xs text-slate-500">Correct</p>
          </div>
          
          <div className="card-bubbly text-center py-4">
            <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-black text-slate-900">{formatTime(totalTime)}</p>
            <p className="text-xs text-slate-500">Time</p>
          </div>
        </div>

        {/* Bonus Rewards Section */}
        <div className="space-y-2">
          {beatYesterdayBonus > 0 && (
            <div data-testid="beat-yesterday-reward" className="bg-amber-50 rounded-2xl p-3 flex items-center gap-3 border border-amber-200">
              <span className="text-2xl">⭐</span>
              <div className="flex-1">
                <p className="font-bold text-amber-800 text-sm">Beat Yesterday!</p>
                <p className="text-xs text-amber-600">You scored higher than yesterday ({yesterdayScore?.correct}/{yesterdayScore?.total})</p>
              </div>
              <span className="font-black text-amber-700 text-lg">+{beatYesterdayBonus}</span>
            </div>
          )}
          {bossResult && (
            <div data-testid="boss-reward-summary" className={`rounded-2xl p-3 flex items-center gap-3 border ${
              bossResult.correct ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
            }`}>
              <span className="text-2xl">🧠</span>
              <div className="flex-1">
                <p className={`font-bold text-sm ${bossResult.correct ? 'text-green-800' : 'text-slate-600'}`}>
                  Brain Boss {bossResult.correct ? 'Defeated!' : 'Challenge'}
                </p>
                <p className="text-xs text-slate-500">{bossResult.correct ? 'Incredible work!' : 'Better luck next time!'}</p>
              </div>
              {bossResult.correct && <span className="font-black text-green-700 text-lg">+30</span>}
            </div>
          )}
        </div>

        {/* Motivational Message */}
        <div className="text-center py-4">
          <p className="text-lg text-slate-600">
            {accuracy >= 80 
              ? "Wow! You're becoming a real brain champion! 🌟"
              : accuracy >= 50
              ? "Great effort! Every puzzle makes you smarter! 💪"
              : "You're learning! Keep practicing and you'll be amazing! 🚀"}
          </p>
        </div>

        {/* Skill Garden Growth Hint */}
        <div className="bg-gradient-to-br from-green-50 to-sky-50 rounded-2xl p-4 text-center">
          <div className="text-3xl mb-2">🌱</div>
          <p className="text-sm text-slate-600">
            Your Skill Garden is growing! Visit it to see your progress.
          </p>
        </div>

        {/* Continue Button */}
        <button
          onClick={onContinue}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid="continue-btn"
        >
          Back to Dashboard
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default SessionSummary;
